import React, { useCallback } from 'react';
import Icon, { SettingFilled } from '@ant-design/icons';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import classNames from 'classnames';
import LoopWidgetProcessor, { LoopState, getDefaultState } from '@kemu-io/kemu-core/widgets/loop/index.js';
import { useIntl } from 'react-intl';
import GateIcon from '../../gateIcon/gateIcon';
import {
	GateCustomSettingsProps,
	GetPortsInformationFunction,
	GateUI,
	GateUIProps
} from '../index.ts';
import useReactiveWidgetState from '../../../common/hooks/useReactiveWidgetState';
import { PortLocation } from '../../../types/canvas_t';
import { SETTINGS_CONTAINER_CLASS } from '../../../common/constants';
import KemuSwitch from '../../form-control/kemuSwitch/KemuSwitch.tsx';
import styles from './loop.module.css';
import { ReactComponent as LoopIcon } from './icon.svg';
import { useTranslation } from '@hooks/index.ts';

type Props = GateUIProps;

const LoopWidget = (props: Props): React.JSX.Element => {
	const t = useTranslation('LogicMapper.Widgets.Loop');

	return (
		<div className={`${styles.GateBody}`}>
			<span className={styles.SettingsBtn} onClick={props.openCustomSettings}>
				<SettingFilled />
			</span>
			<div className={styles.InnerBody}>
				<Icon component={LoopIcon} className={styles.LoopIcon}/>
			</div>
		</div>
	);
};

const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={LoopIcon} />}/>
	);
};

const LoopCustomSettings = (props: GateCustomSettingsProps): React.JSX.Element => {
	const [state, setState] = useReactiveWidgetState<LoopState>(props.recipeId, props.blockId, props.gateInfo.id);
	const t = useTranslation('LogicMapper.Widgets.Loop');

	const handleAutoLoopChange = useCallback((checked: boolean) => {
		setState((current) => ({
			...current,
			autoLoop: checked
		}), true);
	}, [setState]);

	const handleAutoClearChange = useCallback((checked: boolean) => {
		setState((current) => ({
			...current,
			autoClear: checked
		}), true);
	}, [setState]);

	const { autoLoop, autoClear } = state || getDefaultState();

	return (
		<div className={classNames(styles.SettingsContainer, SETTINGS_CONTAINER_CLASS)}>
			{props.children}
			<div className={styles.InputContainer}>
				<label>
					{t('Settings.AutoLoop')}
				</label>
				<KemuSwitch
					checked={autoLoop}
					onChange={handleAutoLoopChange}
					size='small'
				/>
			</div>
			<div className={styles.InputContainer}>
				<label>
					{t('Settings.AutoClear')}
				</label>
				<KemuSwitch
					checked={autoClear}
					onChange={handleAutoClearChange}
					size='small'
				/>
			</div>
		</div>
	);
};

const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<LoopState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = LoopWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = LoopWidgetProcessor.getInputNames(state, portContext);

	const inputPositions: PortLocation[] = [
		[0, 0.2, -1, 0], // array
		[0, 0.5, -1, 0], // next
		[0, 0.8, -1, 0], // clear
	];

	const outputPositions: PortLocation[] = [
		[1, 0.2, 1, 0], // index
		[1, 0.5, 1, 0], // output
		[1, 0.8, 1, 0], // finish
	];

	return {
		outputs: outputNames.map((output, i) => ({
			position: outputPositions[i],
			type: output.type,
			name: output.name,
		})),

		inputs: inputNames.map((input, i) => {
			return  {
				name: input.name,
				type: input.type,
				position: inputPositions[i]
			};
		})
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: LoopWidget,
	settingsCustomSize: true,
	getFloatingLabel: (intl, state) => state.totalItems
		? intl.formatMessage({ id: 'LogicMapper.Widgets.Loop.Label' }, { current: state.currentIndex, total: state.totalItems })
		: intl.formatMessage({ id: 'LogicMapper.Widgets.Loop.LabelNoItems' }),
	CustomSettingsDialog: LoopCustomSettings,
	hasTitle: false,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.Loop.Title' }),
} as GateUI;
